export type RoomAddOn = { label: string, price: string }
export type RoomReview = { quote: string, name: string, avatar?: string }

export type RoomDetail = {
    slug: string
    name: string
    /** Page name for the browser tab, kept short enough to fit next to the site name. */
    seoTitle: string
    seoDescription: string
    bed: string
    /** Leave empty when the occupancy is not published, and the chip is hidden. */
    guests: string
    size: string
    cardImage: string
    cardImageAlt: string
    heroImage: string
    heroImageAlt: string
    overviewImage: string
    overviewImageAlt: string
    galleryImage: string
    galleryImageAlt: string
    featuresImage: string
    cardDescription: string
    overview: string[]
    pricingTitle: string
    pricingSubtitle: string
    price: string
    priceIncludes: string
    addOnsTitle: string
    addOns: RoomAddOn[]
    rating: string
    scores: { label: string, value: string }[]
    reviews: RoomReview[]
}

/** Room and hotel amenities, as listed by the inn. The same in every room. */
export const roomAmenities = [
    "Air Conditioning",
    "Private Bath",
    "Bath and Shower",
    "Balcony or Patio",
    "Color TV",
    "Telephone",
    "Small Refrigerator",
    "Hairdryer",
    "Table with 2 Chairs",
    "Continental Breakfast",
    "Wi-Fi in Common Areas",
    "Outdoor Pool (Summer)"
]

/** Lift tickets the inn adds to a room reservation, at its published 2025–26 rates. */
const liftTicketAddOns: RoomAddOn[] = [
    { label: "Non-peak adult lift ticket, Monday to Friday", price: '$145' },
    { label: "Weekend and peak day adult lift ticket", price: '$165' }
]

/** Room rates are not published, so every room points guests to the front desk. */
const callForRates = {
    pricingTitle: "Call for current rates",
    pricingSubtitle: "Give us a call or email us with your dates and we will quote your stay.",
    price: "Call for rates",
    addOnsTitle: "Lift ticket add-ons, 2025–26 season",
    addOns: liftTicketAddOns,
    rating: '',
    scores: [],
    reviews: []
}

export const roomDetails: RoomDetail[] = [
    {
        slug: 'one-queen-room',
        name: "One Queen Room",
        seoTitle: "One queen room in Killington, VT",
        seoDescription: "A 325 square foot air-conditioned room with one queen bed, a private bath and a balcony or patio, five minutes from the Killington ski lifts.",
        bed: "1 Queen Bed",
        guests: '',
        size: "325 sq ft + balcony",
        cardImage: '/images/greenbrier/inn-room-1.jpg',
        cardImageAlt: "Guest room with one queen bed, a sofa and a table with two chairs",
        heroImage: '/images/greenbrier/inn-room-1.jpg',
        heroImageAlt: "Guest room with one queen bed, a sofa and a table with two chairs",
        overviewImage: '/images/greenbrier/inn-room-view.jpg',
        overviewImageAlt: "Fall foliage and mountains seen from a guest room balcony at the Greenbrier Inn",
        galleryImage: '/images/greenbrier/inn-pool2.jpg',
        galleryImageAlt: "The outdoor pool and lawn at the Greenbrier Inn below fall foliage and mountains",
        featuresImage: '/images/greenbrier/inn-lobby-1.jpg',
        cardDescription: "A very large room with one queen-sized bed, a private bath, a table with two chairs and a private balcony or patio.",
        overview: [
            "is one of our 21 air-conditioned guest rooms: a very large 325 square feet, plus a 45 square foot private balcony or patio.",
            "It comes with one queen-sized bed and a private bath with bath and shower, along with a color TV, telephone, hairdryer, small refrigerator and a table with two chairs.",
            "Connecting rooms are available, and the Killington ski lifts are five minutes away."
        ],
        priceIncludes: "One queen-sized bed, a private bath and a balcony or patio. Call the hotel at 802-775-1608 or send us your dates.",
        ...callForRates
    },
    {
        slug: 'two-queen-room',
        name: "Two Queen Room",
        seoTitle: "Two queen room in Killington, VT",
        seoDescription: "Two queen beds in a 325 square foot air-conditioned room with a private bath and a balcony or patio at the Greenbrier Inn in Killington, Vermont.",
        bed: "2 Queen Beds",
        guests: '',
        size: "325 sq ft + balcony",
        cardImage: '/images/greenbrier/inn-room-2-a.jpg',
        cardImageAlt: "Guest room with two queen beds and a table with two chairs by the window",
        heroImage: '/images/greenbrier/inn-room-2-a.jpg',
        heroImageAlt: "Guest room with two queen beds and a table with two chairs by the window",
        overviewImage: '/images/greenbrier/inn-room-2.jpg',
        overviewImageAlt: "Two queen beds with floral bedspreads in a Greenbrier Inn guest room",
        galleryImage: '/images/greenbrier/inn-exterior-3.jpg',
        galleryImageAlt: "Snowy entrance of the Greenbrier Inn with a wreath and lit evergreen trees",
        featuresImage: '/images/greenbrier/inn-lobby-4.jpg',
        cardDescription: "The same generous 325 square feet with two queen-sized beds, a private bath and a private balcony or patio.",
        overview: [
            "gives you two queen-sized beds in one of our very large air-conditioned rooms: 325 square feet, plus a 45 square foot private balcony or patio.",
            "Every room has a private bath with bath and shower, a color TV, telephone, hairdryer, small refrigerator and a table with two chairs.",
            "Connecting rooms are available too, making the Greenbrier an ideal ski vacation destination five minutes from the Killington lifts."
        ],
        priceIncludes: "Two queen-sized beds, a private bath and a balcony or patio. Call the hotel at 802-775-1608 or send us your dates.",
        ...callForRates
    }
]

export const getRoom = (slug: string): RoomDetail | undefined =>
    roomDetails.find((room) => room.slug === slug)
